/**
 * Apply-email validation — format + MX check for `jobs.apply_email`.
 *
 * 92% of stored `apply_email` values are `apply_email_guessed = 1` (web-search /
 * heuristic leads, live audit 2026-07-01). Emailing a bad address is worse than
 * not emailing at all: it bounces, burns the sending domain's reputation, and
 * silently drops the application. So a guessed email should only be TRUSTED as
 * the `email_direct` channel (and only auto-sent) once it clears:
 *
 *   1. RFC-ish syntax, and
 *   2. an MX record on its domain (the domain can actually receive mail).
 *
 * MX is checked via DNS-over-HTTPS (Cloudflare `cloudflare-dns.com`) so it works
 * inside a Worker with no `dns` module. Never throws — any lookup failure is
 * treated as "unverified" (fail safe: don't promote a guessed email we couldn't
 * confirm). Format-only validation is offline (`isValidEmailFormat`).
 */

/** Conservative email syntax check — one `@`, a dotted domain, no whitespace. */
export function isValidEmailFormat(email: string): boolean {
	const e = email.trim();
	if (e.length < 6 || e.length > 254) return false;
	// local@domain.tld — no spaces, exactly one @, TLD ≥ 2 chars.
	return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(e);
}

/** Extract the lowercased domain from an email, or null when malformed. */
function domainOf(email: string): string | null {
	const at = email.lastIndexOf("@");
	if (at < 0) return null;
	const domain = email
		.slice(at + 1)
		.trim()
		.toLowerCase();
	return domain.includes(".") ? domain : null;
}

interface DohAnswer {
	Answer?: Array<{ type: number; data: string }>;
}

/**
 * True when `domain` has at least one MX record (type 15). DNS-over-HTTPS via
 * Cloudflare; 3s timeout. Returns false on any error/timeout (fail safe — an
 * unconfirmable domain is treated as not-deliverable rather than trusted).
 */
export async function domainHasMx(domain: string): Promise<boolean> {
	try {
		const res = await fetch(
			`https://cloudflare-dns.com/dns-query?name=${encodeURIComponent(domain)}&type=MX`,
			{
				headers: { accept: "application/dns-json" },
				signal: AbortSignal.timeout(3_000),
			},
		);
		if (!res.ok) return false;
		const json = (await res.json()) as DohAnswer;
		return (json.Answer ?? []).some((a) => a.type === 15 && a.data.length > 0);
	} catch {
		return false;
	}
}

export interface ApplyEmailValidation {
	/** Passes syntax — safe to store, but not necessarily deliverable. */
	formatOk: boolean;
	/** Domain has an MX record — safe to TRUST as the email_direct channel. */
	mxOk: boolean;
}

/**
 * Validate an apply email: format (offline) then MX (DoH, only when format is
 * ok). `mxOk` is the bar for promoting a guessed email to trusted/auto-sendable.
 */
export async function validateApplyEmail(
	email: string | null | undefined,
): Promise<ApplyEmailValidation> {
	if (!email || !isValidEmailFormat(email)) {
		return { formatOk: false, mxOk: false };
	}
	const domain = domainOf(email);
	if (!domain) return { formatOk: true, mxOk: false };
	return { formatOk: true, mxOk: await domainHasMx(domain) };
}
